import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { ApplicationFacade, BannerMessage } from '@ridango/components';
import { BaseComponent } from '@ridango/core';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BannerUtils } from './banner.utils';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'rwidget-banner',
    templateUrl: './banner.component.html',
    styleUrls: ['./banner.component.scss']
})
export class BannerComponent extends BaseComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() public bannerMessage: BannerMessage;

    @ViewChild('titleText', { static: false }) titleText?: ElementRef<HTMLElement>;

    renderedTitle = '';
    isTitleTruncated = false;
    isHighContrast = false;
    infoStyle = false;
    trafficInfoStyle = false;
    warningStyle = false;
    highContrastStyle = false;
    readonly moreWithArrow = `${this.translate.instant('MORE')}\u00A0→`;
    readonly assetsPath$: Observable<string> = this.applicationFacade.assetsPath$;

    private updateTitleDebounceId?: ReturnType<typeof setTimeout>;
    private updateTitleRafId?: number;
    private isTitleUpdateInProgress = false;
    private hasPendingTitleUpdate = false;
    private highContrast$: Observable<boolean> = this.applicationFacade.highContrast$;

    constructor(
        private applicationFacade: ApplicationFacade,
        private router: Router,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit(): void {
        this.updateStyleFlags();
        this.initSubscriptions();
    }

    ngAfterViewInit(): void {
        this.scheduleUpdateRenderedTitle();
    }

    ngOnDestroy(): void {
        this.clearTimeOut();
        this.cancelAnimationFrame();
        super.ngOnDestroy();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.bannerMessage) {
            this.updateStyleFlags();
            this.scheduleUpdateRenderedTitle();
        }
    }

    @HostListener('window:resize')
    onResize(): void {
        this.scheduleUpdateRenderedTitle();
    }

    async redirectToDetailsPage(): Promise<void> {
        await this.router.navigateByUrl(`${this.translate.currentLang}/banner/${this.bannerMessage.id}`);
    }

    onSpaceKeydown(event: KeyboardEvent): void {
        event.preventDefault();
        void this.redirectToDetailsPage();
    }

    get bannerAriaLabel(): string {
        return BannerUtils.buildAriaLabel({
            type: this.bannerMessage?.type,
            title: this.bannerMessage?.title ?? '',
            translate: (key) => this.translate.instant(key)
        });
    }

    private scheduleUpdateRenderedTitle(): void {
        if (this.updateTitleDebounceId) {
            return;
        }

        this.updateTitleDebounceId = setTimeout(() => {
            this.updateTitleDebounceId = undefined;
            if (this.isTitleUpdateInProgress) {
                this.hasPendingTitleUpdate = true;
                return;
            }

            this.updateRenderedTitle();
        }, 120);
    }

    /**
     * Ensures the title is rendered in max 2 lines and that `More ->` is always
     * placed directly after the visible text (or after `...` when truncated).
     *
     * We cannot rely on plain CSS clamping here, because the action label must
     * stay inline with the final visible title, not detached on the right or
     * moved to a new line.
     */
    private updateRenderedTitle(): void {
        if (this.isTitleUpdateInProgress) {
            this.hasPendingTitleUpdate = true;
            return;
        }

        this.isTitleUpdateInProgress = true;
        this.updateTitleRafId = requestAnimationFrame(() => {
            try {
                const sourceTitle = this.bannerMessage?.title ?? '';
                const { renderedTitle, isTitleTruncated } = BannerUtils.measureTitle({
                    titleElement: this.titleText?.nativeElement,
                    sourceTitle,
                    moreLabel: this.moreWithArrow
                });

                this.renderedTitle = renderedTitle;
                this.isTitleTruncated = isTitleTruncated;
            } finally {
                this.isTitleUpdateInProgress = false;
                this.updateTitleRafId = undefined;

                if (this.hasPendingTitleUpdate) {
                    this.hasPendingTitleUpdate = false;
                    this.scheduleUpdateRenderedTitle();
                }
            }
        });
    }

    private initSubscriptions(): void {
        this.highContrast$.pipe(takeUntil(this.componentDestroyed$)).subscribe((value) => {
            this.isHighContrast = value;
            this.updateStyleFlags();
        });
    }

    private clearTimeOut(): void {
        if (this.updateTitleDebounceId) {
            clearTimeout(this.updateTitleDebounceId);
        }
    }

    private cancelAnimationFrame(): void {
        if (this.updateTitleRafId) {
            cancelAnimationFrame(this.updateTitleRafId);
        }
    }

    private updateStyleFlags(): void {
        const { infoStyle, trafficInfoStyle, warningStyle, highContrastStyle } = BannerUtils.getStyleFlags(
            this.bannerMessage?.type,
            this.isHighContrast
        );

        this.infoStyle = infoStyle;
        this.trafficInfoStyle = trafficInfoStyle;
        this.warningStyle = warningStyle;
        this.highContrastStyle = highContrastStyle;
    }
}
